import { DEFAULT_DATE_FORMAT, exampleDateText } from "./utils/dateUtils";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { merge } from "lodash";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useConfigQuery } from "services/staticDataService";

const FormattedDatePickerReportsV3 = forwardRef(
  (
    {
      value,
      onChangeDate,
      helperText,
      clearable = true,
      compact = false,
      type = "date",
      // Optional. When given, a typed date that cannot be read is reported via
      // onInvalidChange(true) and the field shows invalidDateHelperText(example),
      // where example is a sample date in the company format.
      onInvalidChange,
      invalidDateHelperText,
      ...datePickerProps
    },
    ref
  ) => {
    const theme = useTheme();
    const { data: dateFormatConfig } = useConfigQuery("date_format");
    const dateFormat = dateFormatConfig?.date_format ?? null;
    const isMonthPicker = type === "month";
    const format = isMonthPicker ? "MMM YY" : dateFormat ?? DEFAULT_DATE_FORMAT;

    const [isInvalid, setIsInvalid] = useState(false);
    const onInvalidChangeRef = useRef(onInvalidChange);
    onInvalidChangeRef.current = onInvalidChange;
    const updateInvalid = (invalid) => {
      setIsInvalid(invalid);
      onInvalidChangeRef.current?.(invalid);
    };
    // The typed text goes with the field, so it no longer blocks anything.
    useEffect(() => () => onInvalidChangeRef.current?.(false), []);

    const invalidHint =
      isInvalid && invalidDateHelperText
        ? invalidDateHelperText(exampleDateText(format))
        : null;
    const shownHelperText = invalidHint ?? helperText;
    // In compact (toolbar) use the hint sits under the field without making
    // the toolbar taller.
    // The picker's text field takes helper text props via FormHelperTextProps.
    const invalidHintProps = invalidHint
      ? {
          error: true,
          FormHelperTextProps: {
            "data-testid": "date-picker-invalid-hint",
            sx: compact
              ? {
                  position: "absolute",
                  top: "100%",
                  mx: 0,
                  whiteSpace: "nowrap",
                }
              : undefined,
          },
        }
      : {};

    const textFieldProps = compact
      ? {
          size: "small",
          helperText: shownHelperText,
          ...invalidHintProps,
        }
      : {
          variant: "filled",
          slotProps: { inputLabel: { shrink: true } },
          helperText: shownHelperText,
          ...invalidHintProps,
        };

    const defaultProps = {
      ref,
      value: (value || "") === "" ? null : dayjs(value),
      views: isMonthPicker ? ["month", "year"] : undefined,
      slotProps: {
        field: {
          clearable,
        },
        textField: {
          ...textFieldProps,
          ...(datePickerProps?.slotProps?.textField ?? {}),
        },
        actionBar: {
          actions: ["clear"],
          sx: {
            backgroundColor: "transparent",
            paddingTop: 0,
            "& button": {
              color: theme.brand.black,
              backgroundColor: "#f0f0f0",
              "&:hover": {
                backgroundColor: theme.brand.gray.gainsboro,
              },
            },
          },
        },
        layout: {
          sx: {
            ".MuiPickersMonth-monthButton.Mui-selected": {
              color: `${theme.brand.white} !important`,
            },
            ".MuiPickersYear-yearButton.Mui-selected": {
              color: `${theme.brand.white} !important`,
            },
          },
        },
      },
      format,
      sx: { maxWidth: compact ? (clearable ? "14rem" : "10.5rem") : null },
      onChange: (e) => {
        if (e == null) {
          updateInvalid(false);
          onChangeDate(null);
        } else if (dayjs(e).isValid()) {
          updateInvalid(false);
          onChangeDate(dayjs(e).format("YYYY-MM-DD"));
        } else {
          updateInvalid(true);
        }
      },
    };

    const mergedProps = merge({}, defaultProps, datePickerProps);

    return (
      <Stack direction={"column"}>
        <DatePicker {...mergedProps} />
      </Stack>
    );
  }
);

FormattedDatePickerReportsV3.displayName = "FormattedDatePickerReportsV3";

export default FormattedDatePickerReportsV3;
